import { ApiError, NetworkError } from '@/api/client';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { PreviewRow } from '@/api/types';
import { describeConflict, revisionConflicts } from './revisions';

/** Saves a base64 payload as a file in the browser (same pattern as the invoice PDF download). */
export function downloadBase64(filename: string, base64: string, mime: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** Mirrors the server's own limit (512 KiB) so an oversized file is refused before it is uploaded. */
export const MAX_FILE_BYTES = 512 * 1024;

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result ?? '');
      resolve(s.slice(s.indexOf(',') + 1));
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** A fresh idempotency key; the form keeps ONE per submission until it succeeds. */
export function newRequestKey(): string {
  return crypto.randomUUID();
}

const CODE_KEYS: Record<string, TranslationKey> = {
  'SELLER_REQUEST.OWNER_NOT_ALLOWED': 'requests.err.ownerNotAllowed',
  'SELLER_REQUEST.OWNER_REQUIRED': 'requests.err.ownerRequired',
  'SELLER_REQUEST.WAREHOUSE_NOT_ALLOWED': 'requests.err.warehouseNotAllowed',
  'SELLER_REQUEST.NO_WAREHOUSE': 'requests.err.noWarehouse',
  'SELLER_REQUEST.CONTRACT_NOT_ACTIVE': 'requests.err.contractNotActive',
  'SELLER_REQUEST.NUMBER_TAKEN': 'requests.err.numberTaken',
  'SELLER_REQUEST.NOT_CANCELLABLE': 'requests.err.notCancellable',
  'SELLER_REQUEST.REFERENCE_INVALID': 'requests.err.referenceInvalid',
  'SELLER_REQUEST.NOT_DRAFT': 'requests.err.notDraft',
  'SELLER_REQUEST.SHIP_TO_INCOMPLETE': 'requests.err.shipToIncomplete',
  'ORDER.SKU_NOT_OWNED_BY_SELLER': 'requests.err.skuNotYours',
  'ORDER.INVALID_LINE_QTY': 'requests.err.qtyInvalid',
  'ORDER.LINES_REQUIRED': 'requests.err.noLines',
};

const FILE_REASON_KEYS: Record<string, TranslationKey> = {
  NOT_XLSX: 'requests.file.notXlsx',
  TOO_LARGE: 'requests.file.tooLarge',
  MACROS: 'requests.file.macros',
  EXTERNAL_LINKS: 'requests.file.externalLinks',
  EMBEDDED_OBJECTS: 'requests.file.embedded',
  CORRUPT: 'requests.file.corrupt',
  TOO_MANY_ROWS: 'requests.file.tooManyRows',
  NO_SHEET: 'requests.file.noSheet',
  HEADER_NOT_FOUND: 'requests.file.headerNotFound',
  EMPTY: 'requests.file.empty',
};

/** One plain-language sentence for an error the request screens can hit; null = let the generic banner handle it. */
export function describeRequestError(t: TranslateFn, err: unknown): string | null {
  if (err instanceof NetworkError) return t('requests.err.network');
  if (!(err instanceof ApiError)) return null;
  const p = err.params as Record<string, string | undefined>;
  if (err.code === 'ORDER.INSUFFICIENT_STOCK') {
    return t('requests.err.insufficient', { sku: p.skuCode ?? '', available: p.available ?? '0', requested: p.requested ?? '' });
  }
  if (err.code === 'SELLER_REQUEST.REVISION_CONFLICT') {
    const items = revisionConflicts(err) ?? [];
    return items.length > 0 ? items.map((c) => describeConflict(t, c)).join(' ') : null;
  }
  if (err.code === 'SELLER_REQUEST.REVISION_STALE') return t('revise.stale');
  if (err.code === 'SELLER_REQUEST.NOT_EDITABLE') return t('revise.err.notEditable');
  if (err.status === 429) {
    // Nothing was created; the form keeps its lines and its idempotency key, so the same button works again.
    const seconds = Math.max(1, Math.ceil(Number(p.retryAfterMs ?? 60000) / 1000));
    return t('requests.err.tooMany', { seconds });
  }
  if (err.code === 'SELLER_REQUEST.FILE_INVALID') return t(FILE_REASON_KEYS[p.reason ?? ''] ?? 'requests.file.corrupt', { max: p.max ?? '200' });
  if (err.status === 400 && err.code.startsWith('VALIDATION.')) return t('requests.err.checkFields');
  const key = CODE_KEYS[err.code];
  return key ? t(key) : null;
}

const ROW_KEYS: Record<string, TranslationKey> = {
  'ROW.FORMULA': 'requests.row.formula',
  'ROW.IDENTIFIER_MISSING': 'requests.row.identifierMissing',
  'ROW.QTY_INVALID': 'requests.row.qtyInvalid',
  'ROW.SKU_NOT_FOUND': 'requests.row.skuNotFound',
  'ROW.BARCODE_NOT_FOUND': 'requests.row.barcodeNotFound',
  'ROW.BARCODE_AMBIGUOUS': 'requests.row.barcodeAmbiguous',
  'ROW.CODE_BARCODE_MISMATCH': 'requests.row.mismatch',
  'ROW.INSUFFICIENT_STOCK': 'requests.row.insufficient',
};

export function describeRow(t: TranslateFn, row: PreviewRow): string {
  if (row.status === 'MERGED') return t('requests.row.merged', { row: row.mergedIntoRow ?? '' });
  if (row.status === 'OK') return t('requests.row.ok');
  const key = ROW_KEYS[row.error?.code ?? ''] ?? 'requests.row.qtyInvalid';
  return t(key, { ...(row.error?.params ?? {}) });
}
